import { Route as RouteIcon, User } from "lucide-react";
import { useDashboard } from "@/components/layout/AppShell";
import { PageHeader, EmptyState } from "@/components/PageState";
import { SourceBadge } from "@/components/SourceBadge";
import { Skeleton } from "@/components/ui/skeleton";

const PHASE_ACCENT = {
  "0-30": { color: "hsl(32 95% 44%)", label: "Foundation" },
  "31-60": { color: "hsl(224 71% 45%)", label: "Implementation" },
  "61-90": { color: "hsl(160 84% 30%)", label: "Optimisation" },
};

export default function Roadmap() {
  const { data, loading } = useDashboard();
  if (loading) return <Skeleton className="h-96 rounded-2xl" />;

  if (!data?.has_data) {
    return (
      <div>
        <PageHeader eyebrow="Transform" title="90-Day Transformation Roadmap" />
        <EmptyState icon={RouteIcon} title="Your roadmap is generated from your opportunities"
          whatsMissing="No prioritised opportunities yet, so there is nothing to schedule."
          whyItMatters="The roadmap turns priorities into a realistic sequence of what to do in the next 30, 60 and 90 days." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader eyebrow="Transform" title="90-Day Transformation Roadmap"
        description="A realistic, outcome-oriented sequence — foundation first, then implementation, then optimisation.">
        <SourceBadge type="rule" />
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {data.roadmap.map((phase) => {
          const accent = PHASE_ACCENT[phase.phase] || {};
          return (
            <div key={phase.phase} className="rounded-2xl border border-border bg-card" data-testid={`roadmap-phase-${phase.phase}`}>
              <div className="border-b border-border p-5">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: accent.color }} />
                  <span className="font-num text-xs font-semibold uppercase tracking-wider text-muted-foreground">{phase.subtitle}</span>
                </div>
                <h3 className="mt-1.5 font-display text-lg font-semibold">{phase.title}</h3>
              </div>
              <div className="space-y-3 p-5">
                {phase.items.length === 0 && (
                  <p className="rounded-xl border border-dashed border-border bg-background/50 p-4 text-center text-xs text-muted-foreground" data-testid={`roadmap-empty-${phase.phase}`}>
                    No actions scheduled in this phase yet. Add opportunities from the Opportunity Radar to populate it.
                  </p>
                )}
                {phase.items.map((item) => (
                  <div key={item.id} className="rounded-xl border border-border bg-background p-4" data-testid={`roadmap-item-${item.id}`}>
                    <p className="text-sm font-medium leading-relaxed">{item.action}</p>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-1 text-muted-foreground">
                        <User className="h-3 w-3" /> {item.owner}
                      </span>
                      <span className="text-emerald-700 dark:text-emerald-400">→ {item.outcome}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
